import { AltLabel } from '../../TreeViewTypes';
import { DataNode, LabelContext } from '../../data_nodes/types';
import { LabelGenerator, DefaultLabelGenerator } from '../types';
import { filterItemsForInterval } from '../../../utils/statusUtils';
import { getVariantFromStatusAnalysis, createVariantMetadata } from '../../utils/iconVariants';

/**
 * Generate default label for Status nodes
 *
 * Returns the full status description as the default (e.g., "On track to hit next inchstone or milestone")
 */
export const generateStatusDefaultLabel: DefaultLabelGenerator = (
  dataNode: DataNode<any>,
  context: LabelContext,
): string => {
  const data = dataNode.data;

  // Handle data with objective and status
  if (data && data.objective && data.status) {
    const statusCode = data.status.statusCode;

    // Skip if status code is missing or empty (no work planned)
    if (!statusCode || statusCode === '') {
      return 'No status';
    }

    // Determine hasResults and hasKeyResults for THIS SPECIFIC INTERVAL (following StatusTooltip1 logic)
    // Filter results to only those due during this status interval
    let resultsForInterval: any[] = [];
    let hasResults = false;
    let hasKeyResults = false;

    if (data.interval) {
      // Use interval data if available
      resultsForInterval = filterItemsForInterval(
        data.objective.results || [],
        data.interval.start,
        data.interval.end,
      );
    }

    hasResults = resultsForInterval.length > 0;
    hasKeyResults = resultsForInterval.some((result: any) => result.isKeyResult);

    // Status descriptions from StatusTooltip1
    // TODO: Make this a proper util with a single source of truth / DRYness
    const defaultDescriptions = {
      '?': 'To be determined, work may happen here',
      p: 'Work is planned/expected here',
      g: 'On track to hit next inchstone or milestone',
      y: 'Unable to hit next inchstone, but able to hit the next milestone',
      r: 'Next milestone cannot be hit without adjusting timeline, scope, or resources',
    };

    const resultsOnlyDescriptions = {
      '?': 'TBD (This status is not applicable here)',
      p: 'Work is planned/expected here',
      g: 'Inchstone hit, results marking progress towards next milestone are checked',
      y: 'Inchstone not hit, but the next milestone is still achievable',
      r: 'Next milestone cannot be hit without adjusting timeline, scope, or resources',
    };

    const keyResultsDescriptions = {
      '?': 'TBD (This status is not applicable here)',
      p: 'Work is planned/expected here',
      g: 'Milestone hit, key results delivered!',
      y: 'Milestone partially hit, key results partially delivered',
      r: 'Milestone not hit, key results not delivered',
    };

    // Select the appropriate description set
    const descriptions = hasKeyResults
      ? keyResultsDescriptions
      : hasResults && !hasKeyResults
        ? resultsOnlyDescriptions
        : defaultDescriptions;

    // ✅ Set icon variant based on status code
    const statusAnalysis = {
      type: statusCode === 'p' || statusCode === '?' ? ('planning' as const) : ('actual' as const),
      statusCode: statusCode as any,
      date: null,
      displayName:
        statusCode === 'g'
          ? 'green'
          : statusCode === 'y'
            ? 'yellow'
            : statusCode === 'r'
              ? 'red'
              : statusCode === 'p'
                ? 'planned'
                : 'TBD',
    };
    const variant = getVariantFromStatusAnalysis(statusAnalysis);
    if (variant) {
      dataNode.metadata = { ...dataNode.metadata, ...createVariantMetadata(variant) };
    }

    // Return the full description
    const description = descriptions[statusCode as keyof typeof descriptions];
    if (description) {
      return description;
    }
  }

  // ✅ Fallback variant for missing status data
  const variant = 'status-none';
  dataNode.metadata = { ...dataNode.metadata, ...createVariantMetadata(variant) };

  // Fallback
  return 'No status found';
};

/**
 * Generate alt labels for Status nodes
 *
 * Provides options for displaying status information in different formats
 */
export const generateStatusAltLabels: LabelGenerator = (
  dataNode: DataNode<any>,
  context: LabelContext,
): AltLabel[] => {
  const data = dataNode.data;
  const defaultLabel = generateStatusDefaultLabel(dataNode, context);

  const labels: AltLabel[] = [
    {
      key: 'default',
      selectOption: 'Default: Description',
      value: defaultLabel,
    },
  ];

  // If we have objective and status data, provide more options
  if (data && data.objective && data.status) {
    const statusCode = data.status.statusCode;

    // Skip if status code is missing or empty (no work planned)
    if (!statusCode || statusCode === '') {
      return labels;
    }

    // Color mapping
    const colorMap: { [key: string]: string } = {
      g: 'green',
      y: 'yellow',
      r: 'red',
      p: 'gray',
      '?': 'gray',
    };

    // Add color alt label
    const color = colorMap[statusCode];
    if (color) {
      labels.push({
        key: 'color',
        selectOption: 'Color',
        value: color,
      });
    }

    // Add color code alt label
    labels.push({
      key: 'color-code',
      selectOption: 'Color code',
      value: statusCode,
    });
  }

  return labels;
};
